import { createSelector } from "@reduxjs/toolkit";
import { selectRegistrationPossibleCountries } from "@nxplatform/geo-data/slices/all-countries.slice";
import { getIpDataState, selectCustomerIpCountry, selectIpDataEntities } from "@nxplatform/geo-data/slices/ip-data.slice";
import { DevFeaturesFromParams, DevFeatures } from "@nxplatform/helpers/dev-features";
import { getPrimaryLang, platformDeepLink, getBaseLocale, BETBUILDER_DEFAULT_LOCALE, getBaseLangIso } from "@nxplatform/helpers/helpers-pure";
import { LOCALE_SETUP_FEATURE_KEY } from "./locale-setup.shared.js";
import { selectIsUserRestrictionsAvailable, selectIsHorseRacingViewType, selectGrpcIsActiveByConfig } from "./platform-settings.selectors.js";
const getLocaleSetupState = (rootState) => rootState[LOCALE_SETUP_FEATURE_KEY];
const selectLocaleSetupBaseCurrency = createSelector(
  getLocaleSetupState,
  (s) => s.entity.baseCurrency
);
const selectOnlyActiveMarkets = createSelector(
  getLocaleSetupState,
  (s) => s.onlyActiveMarkets
);
const selectLocaleSetupEntity = createSelector(
  getLocaleSetupState,
  (s) => s.entity
);
const selectHeaderVisibility = createSelector(
  getLocaleSetupState,
  (s) => s.header
);
const selectSetupTranslations = createSelector(
  getLocaleSetupState,
  (s) => s.translations
);
const selectWsEndpoint = createSelector(
  getLocaleSetupState,
  (s) => s.env.wsEndpoint
);
const selectPlatformApiUrl = createSelector(
  getLocaleSetupState,
  (s) => s.env.platformUrl
);
const selectVipProgramPrize = createSelector(
  getLocaleSetupState,
  (s) => s.env.sportVipWidgetPrize
);
const selectPlatformName = createSelector(
  getLocaleSetupState,
  (s) => s.env.platformName
);
const getLiveChatLicense = createSelector(
  getLocaleSetupState,
  (s) => s.entity.chatId
);
const selectPgwEndpoint = createSelector(
  getLocaleSetupState,
  (s) => s.env.pgwcashier
);
const selectAdditionalCashierLink = createSelector(
  getLocaleSetupState,
  (s) => {
    return s.env.additionalCashierLink;
  }
);
const selectPromotionsVisibility = createSelector(
  getLocaleSetupState,
  (s) => s.env.pageRestrictions
);
const selectHistoryTimeSettings = createSelector(
  getLocaleSetupState,
  (s) => s.env.transactionHistoryTimeSettings
);
const selectBettingHistoryTimeSettings = createSelector(
  getLocaleSetupState,
  (s) => s.env.bettingHistoryTimeSettings
);
const selectBuyCryptoTab = createSelector(
  getLocaleSetupState,
  (s) => {
    var _a, _b;
    return (_b = (_a = s.env.crypto) == null ? void 0 : _a.payments) == null ? void 0 : _b.buyCrypto;
  }
);
const selectBetslipSumConverter = createSelector(
  getLocaleSetupState,
  (s) => {
    var _a, _b;
    return (_b = (_a = s.env.crypto) == null ? void 0 : _a.betslipSumConverter) == null ? void 0 : _b.enabled;
  }
);
const selectIsAnnualReport = createSelector(getLocaleSetupState, (s) => {
  return s.env.annualReport;
});
const selectIsNewAnnualReport = createSelector(
  getLocaleSetupState,
  (s) => s.env.newAnnualReport
);
const selectHiddenFieldsByCountry = createSelector(
  getLocaleSetupState,
  (s, country) => country,
  (s, country) => {
    if (s.env.hideRegistrationFields) {
      if (s.env.hideRegistrationFields[country]) {
        return s.env.hideRegistrationFields[country];
      }
      if (s.env.hideRegistrationFields["default"]) {
        return s.env.hideRegistrationFields["default"];
      }
    }
    return [];
  }
);
const selectOneStepRegistrationOrderBlocks = createSelector(
  getLocaleSetupState,
  (s, country) => country,
  (s, country) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _i;
    if ((_a = s.env) == null ? void 0 : _a.oneStepRegistrationOrderBlocks) {
      if ((_c = (_b = s.env) == null ? void 0 : _b.oneStepRegistrationOrderBlocks) == null ? void 0 : _c[country]) {
        return (_e = (_d = s.env) == null ? void 0 : _d.oneStepRegistrationOrderBlocks) == null ? void 0 : _e[country];
      }
      if ((_g = (_f = s.env) == null ? void 0 : _f.oneStepRegistrationOrderBlocks) == null ? void 0 : _g["default"]) {
        return (_i = (_h = s.env) == null ? void 0 : _h.oneStepRegistrationOrderBlocks) == null ? void 0 : _i["default"];
      }
    }
    return [];
  }
);
const isDisabledPromotions = createSelector(
  getIpDataState,
  selectIsUserRestrictionsAvailable,
  (ipData, userRestrictionsAvailable) => {
    const { internalCountry } = ipData.entities || {};
    const { userRestrictions } = ipData.customer || {};
    const { isBlockedPromotions } = internalCountry || {};
    return !!(isBlockedPromotions || userRestrictionsAvailable && userRestrictions.promo.isBlockedPromotions);
  }
);
const selectForecastEnabled = createSelector(
  getLocaleSetupState,
  (s) => s.env.forecastDisabled
);
const isCountryFinland = createSelector(getIpDataState, (ipData) => {
  const { country } = ipData.entities || {};
  return country && country === "FI";
});
const selectPendingFeaturesByName = createSelector(
  getLocaleSetupState,
  (s, name) => name,
  (s, name) => {
    var _a, _b;
    if ((_b = (_a = s.env.pendingFeatures) == null ? void 0 : _a[name]) == null ? void 0 : _b.active) {
      return { active: true };
    }
    const featuresFromUrl = s.env.activateByUrlParam;
    if (featuresFromUrl && name) {
      const isActivatedByUrlParam = featuresFromUrl.split(",").some((feature) => DevFeaturesFromParams[feature] === name);
      if (isActivatedByUrlParam) {
        return { active: true };
      }
    }
    return { active: false };
  }
);
const selectSportFeaturesConfig = createSelector(
  getLocaleSetupState,
  (s) => s.env.sportFeatures || {}
);
const selectHorseRacingByEnvEnabled = createSelector(
  selectSportFeaturesConfig,
  (s) => {
    var _a;
    return !!((_a = s.horseRacing) == null ? void 0 : _a.enabled);
  }
);
const selectShowLatrobetByGeo = createSelector(
  [
    selectIsHorseRacingViewType,
    selectRegistrationPossibleCountries,
    selectCustomerIpCountry
  ],
  (racingViewType, contriesList, countryIp) => {
    const countryData = contriesList.find(
      (country) => country.alpha_2 === countryIp
    );
    if (racingViewType === "iframe") {
      return true;
    }
    if (racingViewType !== "iframe" && countryData) {
      return !!countryData.racingEnabledForGeo;
    }
    return false;
  }
);
const selectShowHorseRacing = createSelector(
  selectIpDataEntities,
  (ipData) => {
    var _a;
    return !!((_a = ipData == null ? void 0 : ipData.internalCountry) == null ? void 0 : _a.racingEnabledForGeo);
  }
);
const selectRealLogos = createSelector(
  getLocaleSetupState,
  (s) => s.realLogo
);
const selectLocaleSetupLanguageList = createSelector(
  getLocaleSetupState,
  (s) => s.entity.languageList
);
const selectSrtmCommands = createSelector(
  getLocaleSetupState,
  (s) => !!window.srtmCommands
);
const primaryLang = getPrimaryLang();
const selectLocaleSetupLanguagesRoutes = createSelector(
  selectLocaleSetupLanguageList,
  (languages) => Object.values(languages).filter((lang) => lang.url !== primaryLang).map((lang) => lang.url).join("|")
);
const getLanguageIcon = (lang) => {
  var _a;
  const splitted = ((_a = lang == null ? void 0 : lang.locale) == null ? void 0 : _a.toLowerCase().split("_")) || "";
  if (splitted.length > 1) {
    return splitted[1];
  }
  return splitted[0] ?? null;
};
const selectLocaleSetupLanguageListDropDown = createSelector(
  selectLocaleSetupLanguageList,
  (languages) => Object.values(languages).map((lang) => ({
    text: lang.name,
    shortName: lang.short_name,
    icon: getLanguageIcon(lang),
    flagImage: lang.flag_image,
    url: lang.url,
    id: lang.url,
    locale: lang.locale,
    iso: lang.iso
  }))
);
const selectLocaleSetupPrimaryLanguage = createSelector(
  getLocaleSetupState,
  (s) => s.entity.primaryLanguage
);
const selectLocaleSetupEntityLanguage = createSelector(
  getLocaleSetupState,
  (s) => s.entity.baseLanguage
);
const selectBaseLanguageIso = () => getBaseLangIso();
const selectLocaleSetupEntityActiveLanguageData = createSelector(
  [selectLocaleSetupLanguageListDropDown, selectLocaleSetupEntityLanguage],
  (languages, currentLanguageUrl) => (languages || []).find((lang) => lang.url === currentLanguageUrl)
);
const selectLocaleSetupEntityLocale = createSelector(
  getLocaleSetupState,
  (s) => s.entity.locale
);
const selectLocaleSetupSlugLanguage = createSelector(
  getLocaleSetupState,
  (s) => s.entity.baseLanguage === s.entity.primaryLanguage ? "/" : `/${s.entity.baseLanguage}/`
);
const selectLocaleSetupLoaded = createSelector(
  getLocaleSetupState,
  (s) => s.loaded
);
const selectLocaleSetupError = createSelector(
  getLocaleSetupState,
  (s) => s.error
);
const selectYandexMetricaAccountId = createSelector(
  getLocaleSetupState,
  (s) => s.env.yandexMetrikaAccountId
);
const selectRecaptchaKey = createSelector(
  getLocaleSetupState,
  (s) => s.env.reCaptchaKey
);
const selectImageCdn = createSelector(
  getLocaleSetupState,
  (s) => s.env.imageCdn
);
const selectCashierImageCdn = createSelector(
  getLocaleSetupState,
  (s) => s.env.cashierImageCdn
);
const selectBetradarStatisticUrl = createSelector(
  getLocaleSetupState,
  (s) => s.env.betradarStatisticUrl
);
const selectFooterMenu = createSelector(
  getLocaleSetupState,
  (s) => s.entity.footerMenu || []
);
const selectFooterInfoContent = createSelector(
  getLocaleSetupState,
  (s) => s.entity.footerInfoContent
);
const selectMerchantId = createSelector(
  getLocaleSetupState,
  (s) => s.env.merchantId || ""
);
const selectProducersIconUrl = createSelector(
  getLocaleSetupState,
  (s) => s.env.producersIconUrl
);
const selectCashierModalTheme = createSelector(
  getLocaleSetupState,
  (s) => s.env.cashierModalTheme
);
const selectCashierEnableSelectorCurrency = createSelector(
  getLocaleSetupState,
  (s) => s.env.cashierEnableSelectorCurrency
);
const selectIsEparkstsEnabled = createSelector(
  getLocaleSetupState,
  (s) => {
    var _a;
    return (_a = s.env.features) == null ? void 0 : _a.eParaksts;
  }
);
const selectDeepLink = createSelector(
  getLocaleSetupState,
  (s, url) => url,
  (s, url) => platformDeepLink(s.env.mobAppDomain, url)
);
const selectDepositType = createSelector(
  getLocaleSetupState,
  (s) => s.depositType
);
const selectProjectBaseCurrency = createSelector(
  getLocaleSetupState,
  (s) => s.projectBaseCurrency
);
const selectRacingPageShowLeftSidebar = createSelector(
  selectSportFeaturesConfig,
  (s) => {
    var _a, _b;
    return !!((_b = (_a = s.horseRacing) == null ? void 0 : _a.racingPage) == null ? void 0 : _b.showLeftSidebar);
  }
);
const selectRacingPageExternalResources = createSelector(
  selectSportFeaturesConfig,
  (s) => {
    var _a, _b;
    return ((_b = (_a = s.horseRacing) == null ? void 0 : _a.racingPage) == null ? void 0 : _b.externalResources) || [];
  }
);
const selectMobileAppPageConfig = createSelector(
  getLocaleSetupState,
  (s) => s.env.mobileAppPage
);
const selectIsOneTrustEnabled = createSelector(
  getLocaleSetupState,
  (s) => {
    var _a;
    return (_a = s.env.oneTrust) == null ? void 0 : _a.enabled;
  }
);
const selectOneTrustExternalResources = createSelector(
  getLocaleSetupState,
  (s) => {
    var _a;
    return (_a = s.env.oneTrust) == null ? void 0 : _a.externalResources;
  }
);
const selectMobileSportMenuTopLeaguesNumber = createSelector(
  getLocaleSetupState,
  (s) => s.env.mobileSportMenuTopLeaguesNumber
);
const selectPendingFeatures = createSelector(
  getLocaleSetupState,
  (s) => s.env.pendingFeatures
);
const selectShowRealTimeIncomingBets = createSelector(
  getLocaleSetupState,
  (s) => !!s.env.showRealTimeIncomingBets
);
const selectWeeklyForecast = createSelector(
  selectSportFeaturesConfig,
  (s) => s.weeklyForecast || {}
);
const selectMyBrandsLogo = createSelector(
  selectSportFeaturesConfig,
  (s) => s.myBetsBrandLogo
);
const selectFavoritesAndPinsEnabled = createSelector(
  selectSportFeaturesConfig,
  (s) => {
    var _a;
    return !!((_a = s.favoritesAndPins) == null ? void 0 : _a.enabled);
  }
);
const selectShowFavoritesAndPins = createSelector(
  [
    (state) => {
      var _a;
      return (_a = selectPendingFeaturesByName(state, DevFeatures.ST137)) == null ? void 0 : _a.active;
    },
    selectFavoritesAndPinsEnabled
  ],
  (featureFromParams, favoritesAndPinsEnabled) => featureFromParams || favoritesAndPinsEnabled
);
const selectNewSearchV2Enabled = createSelector(
  selectSportFeaturesConfig,
  (s) => !!s.newSearchEnabled
);
const selectShowNewSearchV2 = createSelector(
  [
    (state) => {
      var _a;
      return (_a = selectPendingFeaturesByName(state, DevFeatures.ST125)) == null ? void 0 : _a.active;
    },
    selectNewSearchV2Enabled
  ],
  (featureFromParams, newSearchV2Enabled) => featureFromParams || newSearchV2Enabled
);
const selectHideMainPageBanners = createSelector(
  getLocaleSetupState,
  (s) => s.env.hideMainPageBanners
);
const selectOddsBoosterEnabled = createSelector(
  selectSportFeaturesConfig,
  (s) => {
    var _a;
    return (_a = s == null ? void 0 : s.oddsBooster) == null ? void 0 : _a.enabled;
  }
);
const selectBroadCastPlugEnabled = createSelector(
  selectSportFeaturesConfig,
  (s) => !!(s == null ? void 0 : s.broadCastPlug)
);
const selectRiskFreeBetEventsHighlightEnabled = createSelector(
  selectSportFeaturesConfig,
  (s) => !!(s == null ? void 0 : s.riskFreeBetEventsHighlight)
);
const selectBetslipPrintEnabled = createSelector(
  selectSportFeaturesConfig,
  (s) => !!(s == null ? void 0 : s.betslipPrintEnabled)
);
const selectIncomingBetsEnabled = createSelector(
  selectSportFeaturesConfig,
  (s) => !!(s == null ? void 0 : s.incomingBetsEnabled)
);
const selectOddsBoosterBackgrounds = createSelector(
  selectSportFeaturesConfig,
  (s) => {
    var _a;
    return ((_a = s.oddsBooster) == null ? void 0 : _a.backgrounds) || {};
  }
);
const selectNewForecastConfigEnabled = createSelector(
  selectSportFeaturesConfig,
  (s) => {
    var _a;
    return (_a = s.forecast) == null ? void 0 : _a.enabled;
  }
);
const selectNewForecastMainBannerDefault = createSelector(
  selectSportFeaturesConfig,
  (s) => {
    var _a, _b;
    return (_b = (_a = s.forecast) == null ? void 0 : _a.mainBanner) == null ? void 0 : _b.img;
  }
);
const selectNewForecastSideBannersDefault = createSelector(
  selectSportFeaturesConfig,
  (s) => {
    var _a;
    return (_a = s.forecast) == null ? void 0 : _a.sideMenuImg;
  }
);
const selectMobileAppsSettings = createSelector(
  getLocaleSetupState,
  (s) => s.env.mobileApps || {}
);
const selectAndroidAppSettings = createSelector(
  selectMobileAppsSettings,
  (s) => s.android
);
const selectIOsAppSettings = createSelector(
  selectMobileAppsSettings,
  (s) => s.iOs
);
const selectNewBetSlipMobile = createSelector(
  getLocaleSetupState,
  (s) => s.env.newBetSlipMobile
);
const selectNewCoboboostDesignActive = createSelector(
  selectNewBetSlipMobile,
  (s) => s == null ? void 0 : s.newComboBoostDisplayEnabled
);
const selectNewBetSlipMobileEnabled = createSelector(
  selectNewBetSlipMobile,
  (s) => {
    const isMobile = !!window.isMobile;
    const newMobBetslipDesignActive = isMobile && (s == null ? void 0 : s.enabled);
    return newMobBetslipDesignActive;
  }
);
const selectRedirectToMyBetsEnabled = createSelector(
  selectNewBetSlipMobile,
  (s) => {
    const isMobile = !!window.isMobile;
    const redirectToMyBets = isMobile && (s == null ? void 0 : s.onSuccessBetRedirectToMyBets);
    return redirectToMyBets;
  }
);
const selectNewBetShareModalEnabled = createSelector(
  selectNewBetSlipMobile,
  (s) => {
    return s == null ? void 0 : s.enabled;
  }
);
const selectThemeAlphaDesign = createSelector(
  getLocaleSetupState,
  (s) => {
    return s.env.themeAlpha;
  }
);
const selectBonusHeader = createSelector(getLocaleSetupState, (s) => {
  return s.env.bonusHeader;
});
const selectThemeAlphaByParams = createSelector(
  [(state) => {
    var _a;
    return (_a = selectPendingFeaturesByName(state, DevFeatures.ST2312)) == null ? void 0 : _a.active;
  }],
  (featureFromParams) => featureFromParams
);
const selectS6GameImagesEnabled = createSelector(
  getLocaleSetupState,
  (s) => {
    var _a;
    return (_a = s.env.s6Images) == null ? void 0 : _a.enabled;
  }
);
const selectS6GameDefaultCustomImg = createSelector(
  getLocaleSetupState,
  (s) => {
    var _a;
    return (_a = s.env.s6Images) == null ? void 0 : _a.defaultCustomImg;
  }
);
const selectIsBetBuilderActive = createSelector(
  [
    selectSportFeaturesConfig,
    (state) => {
      var _a;
      return (_a = selectPendingFeaturesByName(state, DevFeatures.ST395)) == null ? void 0 : _a.active;
    }
  ],
  (s, featureFromParams) => {
    var _a;
    return ((_a = s.betBuilder) == null ? void 0 : _a.enabled) || featureFromParams;
  }
);
const selectBetBuilderLocales = createSelector(
  selectSportFeaturesConfig,
  (s) => {
    var _a;
    return ((_a = s.betBuilder) == null ? void 0 : _a.locales) || [];
  }
);
const formatLocaleForBetBuilder = (locale) => locale.replace("_", "-");
const selectBetBuilderLocaleBySportId = createSelector(
  selectBetBuilderLocales,
  (locales, sportId) => sportId,
  (locales, sportId) => {
    const locale = getBaseLocale();
    const formattedLocale = formatLocaleForBetBuilder(locale);
    const betBuilderLocale = locales.find(
      (locale2) => locale2.iso === formattedLocale
    );
    if (betBuilderLocale == null ? void 0 : betBuilderLocale.sportIds.includes(Number(sportId))) {
      return betBuilderLocale.iso;
    }
    const [laxLocale] = locale.split("_");
    const betBuilderLocaleLax = locales.find(
      (locale2) => locale2.iso.includes(laxLocale) && locale2.sportIds.includes(Number(sportId))
    );
    if (betBuilderLocaleLax) {
      return betBuilderLocaleLax.iso;
    }
    return BETBUILDER_DEFAULT_LOCALE;
  }
);
const selectGamixterSettings = createSelector(
  getLocaleSetupState,
  (s) => s.env.gamixter
);
const selectGrpcIsActive = createSelector(
  [selectGrpcIsActiveByConfig, (state) => {
    var _a;
    return (_a = selectPendingFeaturesByName(state, DevFeatures.ST3606)) == null ? void 0 : _a.active;
  }],
  (grpcIsActiveByConfig, grpcIsActiveByFrontSettings) => grpcIsActiveByConfig && grpcIsActiveByFrontSettings
);
export {
  getLiveChatLicense,
  getLocaleSetupState,
  isCountryFinland,
  isDisabledPromotions,
  selectAdditionalCashierLink,
  selectAndroidAppSettings,
  selectBaseLanguageIso,
  selectBetBuilderLocaleBySportId,
  selectBetBuilderLocales,
  selectBetradarStatisticUrl,
  selectBetslipPrintEnabled,
  selectBetslipSumConverter,
  selectBettingHistoryTimeSettings,
  selectBonusHeader,
  selectBroadCastPlugEnabled,
  selectBuyCryptoTab,
  selectCashierEnableSelectorCurrency,
  selectCashierImageCdn,
  selectCashierModalTheme,
  selectDeepLink,
  selectDepositType,
  selectFavoritesAndPinsEnabled,
  selectFooterInfoContent,
  selectFooterMenu,
  selectForecastEnabled,
  selectGamixterSettings,
  selectGrpcIsActive,
  selectHeaderVisibility,
  selectHiddenFieldsByCountry,
  selectHideMainPageBanners,
  selectHistoryTimeSettings,
  selectHorseRacingByEnvEnabled,
  selectIOsAppSettings,
  selectImageCdn,
  selectIncomingBetsEnabled,
  selectIsAnnualReport,
  selectIsBetBuilderActive,
  selectIsEparkstsEnabled,
  selectIsNewAnnualReport,
  selectIsOneTrustEnabled,
  selectLocaleSetupBaseCurrency,
  selectLocaleSetupEntity,
  selectLocaleSetupEntityActiveLanguageData,
  selectLocaleSetupEntityLanguage,
  selectLocaleSetupEntityLocale,
  selectLocaleSetupError,
  selectLocaleSetupLanguageList,
  selectLocaleSetupLanguageListDropDown,
  selectLocaleSetupLanguagesRoutes,
  selectLocaleSetupLoaded,
  selectLocaleSetupPrimaryLanguage,
  selectLocaleSetupSlugLanguage,
  selectMerchantId,
  selectMobileAppPageConfig,
  selectMobileAppsSettings,
  selectMobileSportMenuTopLeaguesNumber,
  selectMyBrandsLogo,
  selectNewBetShareModalEnabled,
  selectNewBetSlipMobile,
  selectNewBetSlipMobileEnabled,
  selectNewCoboboostDesignActive,
  selectNewForecastConfigEnabled,
  selectNewForecastMainBannerDefault,
  selectNewForecastSideBannersDefault,
  selectNewSearchV2Enabled,
  selectOddsBoosterBackgrounds,
  selectOddsBoosterEnabled,
  selectOneStepRegistrationOrderBlocks,
  selectOneTrustExternalResources,
  selectOnlyActiveMarkets,
  selectPendingFeatures,
  selectPendingFeaturesByName,
  selectPgwEndpoint,
  selectPlatformApiUrl,
  selectPlatformName,
  selectProducersIconUrl,
  selectProjectBaseCurrency,
  selectPromotionsVisibility,
  selectRacingPageExternalResources,
  selectRacingPageShowLeftSidebar,
  selectRealLogos,
  selectRecaptchaKey,
  selectRedirectToMyBetsEnabled,
  selectRiskFreeBetEventsHighlightEnabled,
  selectS6GameDefaultCustomImg,
  selectS6GameImagesEnabled,
  selectSetupTranslations,
  selectShowFavoritesAndPins,
  selectShowHorseRacing,
  selectShowLatrobetByGeo,
  selectShowNewSearchV2,
  selectShowRealTimeIncomingBets,
  selectSportFeaturesConfig,
  selectSrtmCommands,
  selectThemeAlphaByParams,
  selectThemeAlphaDesign,
  selectVipProgramPrize,
  selectWeeklyForecast,
  selectWsEndpoint,
  selectYandexMetricaAccountId
};
